<template>
  <div class="location-generation-progress">
    <!-- Idle State - Show Generate Button -->
    <div v-if="status === 'idle'" class="generation-trigger">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium" style="color: var(--text-primary);">
            Generate Location Pages
          </p>
          <p class="text-sm" style="color: var(--text-secondary);">
            {{ totalExpected }} pages will be generated
          </p>
        </div>
        <button
          type="button"
          @click="startGeneration"
          :disabled="starting"
          class="btn btn-primary">
          <svg v-if="starting" class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          {{ starting ? 'Starting...' : 'Generate Location Pages' }}
        </button>
      </div>
    </div>

    <!-- Generating State -->
    <div v-else-if="status === 'generating'" class="generation-progress">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span class="font-medium text-blue-900">Generating Location Pages</span>
          </div>
          <span class="text-sm text-blue-700">{{ completed }} / {{ total }}</span>
        </div>

        <!-- Progress Bar -->
        <div class="w-full bg-blue-200 rounded-full h-2.5 mb-3">
          <div
            class="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
            :style="{ width: `${progress}%` }" />
        </div>

        <!-- Current Status -->
        <p class="text-sm text-blue-700">
          {{ currentMessage }}
        </p>

        <!-- Recent Activity Log -->
        <div v-if="recentActivity.length > 0" class="mt-3 pt-3 border-t border-blue-200">
          <div class="max-h-32 overflow-y-auto space-y-1">
            <div
              v-for="(activity, index) in recentActivity"
              :key="index"
              class="flex items-center text-xs"
              :class="activityClass(activity)">
              <component :is="activityIcon(activity)" class="w-3 h-3 mr-1.5 flex-shrink-0" />
              <span class="truncate">{{ activity.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Completed State -->
    <div v-else-if="status === 'completed'" class="generation-complete">
      <div
        class="rounded-lg p-4"
        :class="failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg v-if="failed === 0" class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <svg v-else class="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <p class="font-medium" :class="failed > 0 ? 'text-yellow-800' : 'text-green-800'">
              {{ completionMessage }}
            </p>
            <p class="text-sm mt-1" :class="failed > 0 ? 'text-yellow-700' : 'text-green-700'">
              {{ completed }} pages generated successfully
              <span v-if="failed > 0">, {{ failed }} failed</span>
            </p>
          </div>
          <button
            type="button"
            @click="reset"
            class="ml-4 text-sm font-medium"
            :class="failed > 0 ? 'text-yellow-600 hover:text-yellow-800' : 'text-green-600 hover:text-green-800'">
            Dismiss
          </button>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="status === 'error'" class="generation-error">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <p class="font-medium text-red-800">
              Generation Failed
            </p>
            <p class="text-sm text-red-700 mt-1">
              {{ errorMessage }}
            </p>
          </div>
          <button
            type="button"
            @click="reset"
            class="ml-4 text-sm font-medium text-red-600 hover:text-red-800">
            Try Again
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, h } from 'vue'
import consumer from '~/channels/consumer'

const props = defineProps({
  websiteId: {
    type: String,
    required: true
  },
  generateUrl: {
    type: String,
    required: true
  },
  csrfToken: {
    type: String,
    required: true
  },
  locationCount: {
    type: Number,
    default: 0
  },
  serviceLocationCount: {
    type: Number,
    default: 0
  }
})

// State
const status = ref('idle') // idle, generating, completed, error
const starting = ref(false)
const progress = ref(0)
const completed = ref(0)
const failed = ref(0)
const total = ref(0)
const currentMessage = ref('')
const errorMessage = ref('')
const recentActivity = ref([])
const subscription = ref(null)

// Computed
const totalExpected = computed(() => props.locationCount + props.serviceLocationCount)

const completionMessage = computed(() => {
  if (failed.value === 0) {
    return 'Location pages generated successfully!'
  }
  return 'Generation completed with some errors'
})

// Methods
const startGeneration = async() => {
  starting.value = true

  try {
    const response = await fetch(props.generateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': props.csrfToken,
        'Accept': 'application/json'
      }
    })

    const data = await response.json()

    if (response.ok) {
      status.value = 'generating'
      total.value = data.total_pages
      currentMessage.value = 'Starting generation...'
      subscribeToChannel()
    } else {
      status.value = 'error'
      errorMessage.value = data.error || 'Failed to start generation'
    }
  } catch (err) {
    console.error('[LocationGeneration] Failed to start:', err)
    status.value = 'error'
    errorMessage.value = err.message
  } finally {
    starting.value = false
  }
}

const subscribeToChannel = () => {
  if (subscription.value) return

  subscription.value = consumer.subscriptions.create(
    { channel: 'WebsiteGenerationChannel', website_id: props.websiteId },
    {
      connected() {
        console.log('[LocationGeneration] Connected to channel')
      },
      disconnected() {
        console.log('[LocationGeneration] Disconnected from channel')
      },
      received(data) {
        handleMessage(data)
      }
    }
  )
}

const handleMessage = (data) => {
  // Only handle location generation events
  if (data.generation_type !== 'location_pages') return

  switch (data.event) {
    case 'location_generation_started':
      status.value = 'generating'
      total.value = data.total_pages
      progress.value = 0
      completed.value = 0
      failed.value = 0
      currentMessage.value = data.message
      break

    case 'location_page_generating':
      currentMessage.value = data.message
      break

    case 'location_page_completed':
      completed.value = data.completed
      progress.value = data.progress
      currentMessage.value = data.message
      // Show warning for fallback content, success for AI content
      addActivity({
        type: data.content_source === 'fallback' || data.content_source === 'mixed' ? 'warning' : 'success',
        name: data.name
      })
      break

    case 'location_page_failed':
      failed.value = data.failed
      progress.value = data.progress
      addActivity({ type: 'error', name: data.name })
      break

    case 'location_generation_completed':
      status.value = 'completed'
      completed.value = data.completed
      failed.value = data.failed
      progress.value = 100
      currentMessage.value = data.message
      unsubscribe()
      // Reload page after a short delay to show the new pages in the UI
      setTimeout(() => window.location.reload(), 1500)
      break

    case 'location_generation_failed':
      status.value = 'error'
      errorMessage.value = data.error || data.message
      unsubscribe()
      break
  }
}

const addActivity = (activity) => {
  recentActivity.value.unshift(activity)
  // Keep only last 10 items
  if (recentActivity.value.length > 10) {
    recentActivity.value.pop()
  }
}

const activityClass = (activity) => {
  switch (activity.type) {
    case 'success': return 'text-green-600'
    case 'warning': return 'text-yellow-600'
    case 'error': return 'text-red-600'
    default: return 'text-gray-600'
  }
}

const activityIcon = (activity) => {
  if (activity.type === 'success') {
    return {
      render() {
        return h('svg', {
          fill: 'none',
          stroke: 'currentColor',
          viewBox: '0 0 24 24'
        }, [
          h('path', {
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
            'stroke-width': '2',
            d: 'M5 13l4 4L19 7'
          })
        ])
      }
    }
  }
  if (activity.type === 'warning') {
    return {
      render() {
        return h('svg', {
          fill: 'none',
          stroke: 'currentColor',
          viewBox: '0 0 24 24'
        }, [
          h('path', {
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
            'stroke-width': '2',
            d: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
          })
        ])
      }
    }
  }
  return {
    render() {
      return h('svg', {
        fill: 'none',
        stroke: 'currentColor',
        viewBox: '0 0 24 24'
      }, [
        h('path', {
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          'stroke-width': '2',
          d: 'M6 18L18 6M6 6l12 12'
        })
      ])
    }
  }
}

const reset = () => {
  status.value = 'idle'
  progress.value = 0
  completed.value = 0
  failed.value = 0
  total.value = 0
  currentMessage.value = ''
  errorMessage.value = ''
  recentActivity.value = []
}

const unsubscribe = () => {
  if (subscription.value) {
    subscription.value.unsubscribe()
    subscription.value = null
  }
}

// Lifecycle
onMounted(() => {
  // Subscribe immediately in case there's an ongoing generation
  subscribeToChannel()
})

onUnmounted(() => {
  unsubscribe()
})
</script>

<style scoped>
.location-generation-progress {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-muted);
}
</style>
